// tslint:disable: no-unused-expression max-line-length
import { Component, Input, HostListener, SimpleChanges, OnChanges, OnInit, Output, EventEmitter, OnDestroy } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { Subject } from 'rxjs';

import { CallApiMethodsService } from 'src/app/services/AsynchronousRequestServices/call-api-methods.service';
import { SharedAsyncMethodsService } from 'src/app/services/TemplateServices/shared-async-methods.service';
import { GetCurrentThemeService } from 'src/app/services/TemplateServices/get-current-theme.service';
import { SharedFunctionsService } from 'src/app/services/TemplateServices/shared-functions.service';
import { PrettyTooltipService } from 'src/app/services/TemplateServices/pretty-tooltip.service';

import { GetDatatableHidingFieldsPipe } from 'src/app/pipes/get-datatable-hiding-fields.pipe';
import { GetBooleanResultPipe } from 'src/app/pipes/get-boolean-result.pipe';
import { NormalizeTextPipe } from 'src/app/pipes/normalize-text.pipe';
import { UncommentHtmlPipe } from 'src/app/pipes/uncomment-html.pipe';
import { ArraySlicePipe } from 'src/app/pipes/array-slice.pipe';
import { SetValuePipe } from 'src/app/pipes/set-value.pipe';
import { SetFillPipe } from 'src/app/pipes/set-fill.pipe';

import { REGEXP } from 'src/app/constants/reg-exp';

import { Tables, Headers, Body, Footers, Options, Responsive, Datatable, Triggers, TableFile, AsyncData, RowStructure, Field, ColumnStructure, ExportStructure } from 'src/app/interfaces/datatable';
import { AlphanumericValues, AnyValues } from 'src/app/interfaces/undetermined';
import { ErrorStructure } from 'src/app/interfaces/management';

@Component({
  providers: [NormalizeTextPipe, UncommentHtmlPipe, GetDatatableHidingFieldsPipe, SetFillPipe, GetBooleanResultPipe, ArraySlicePipe],
  templateUrl: './datatable.component.html',
  styleUrls: ['./datatable.component.scss'],
  selector: 'app-datatable',
})
export class DatatableComponent implements OnInit, OnChanges, OnDestroy {

  // DatatableClass: Estructura inicial del datatable
  DatatableClass: Datatable = {
    table: {
      tabulated: true,
      classes: "",
      styles: "",
    },
    headers: {
      buttonOptions: {
        right: {
          columns: {},
          rows: {},
        },
        left: {
          columns: {},
          rows: {},
        }
      },
      async: {
        show: false,
        fields: [],
      },
      fixed: false,
      classes: "",
      columns: {},
      buttons: [],
      styles: "",
      fields: [],
      rows: {},
    },
    body: {
      buttonOptions: {
        right: {
          columns: {},
          rows: {},
        },
        left: {
          columns: {},
          rows: {},
        }
      },
      async: {
        show: false,
        fields: [],
      },
      classes: "",
      columns: {},
      buttons: [],
      fields: [],
      styles: "",
      rows: {},
    },
    footers: {
      buttonOptions: {
        right: {
          columns: {},
          rows: {},
        },
        left: {
          columns: {},
          rows: {},
        }
      },
      async: {
        show: false,
        fields: [],
      },
      fixed: false,
      classes: "",
      columns: {},
      buttons: [],
      styles: "",
      fields: [],
      rows: {},
    },
    options: {
      pager: {
        show: false,
        limit: 10,
        page: 1,
      },
      searcher: {
        column: ["all"],
        timeInterval: 0,
        interval: null,
        show: false
      },
      sorter: {
        show: false,
        column: "0"
      },
      hiding: {
        reference: 0,
        show: false,
        groups: {}
      },
      download: {
        show: true,
        files: []
      },
      tools: {
        collapsible: false,
        sticky: false,
        panel: false
      }
    },
    responsive: [],
  };

  // config: Listado de estructura datatable de acuerdo al responsive
  config: Responsive[] = [];
  // datatable: Estructura seleccionada a utilizar
  datatable: Datatable;
  // index: Índice de la estructura seleccionada en el listado
  index = 0;
  // instanceId: Id único de la instancia del datatable
  @Input() instanceId: string;

  // Opciones del formulario de descarga de tabla
  filterOptions: FormGroup;
  // formOptions: Estructura dl formulario
  formOptions: FormGroup;

  // activate: Emisor que agrupa todos los eventos que se pueden devolver al usuario
  @Output() activate: EventEmitter<{ [event: string]: Subject<any> }> = new EventEmitter();

  // fileAction: Comparte información de la descarga, si está definido la descarga se completo, de lo contrario se canceló
  fileAction: Subject<boolean> = new Subject();
  // rowAction: Comparte información de la fila selecionada
  rowAction: Subject<({ row: ((string | number | null)[] | AlphanumericValues), event: (string | number), action: string }) | null> = new Subject();
  // structure: Comparte información de descarga
  structure: Subject<ExportStructure> = new Subject();
  // searchForm: Comparte información de formulario
  searchForm: Subject<boolean> = new Subject();
  // searchFormChanges: Comparte información de formulario cuando se actualiza
  searchFormChanges: Subject<{ keywords: string, fields: Field[], instance: DatatableComponent }> = new Subject();
  // buttonClicked: Comparte información cuando un boton se presiona
  buttonClicked: Subject<object> = new Subject();

  // w: Instancia de la propiedad windows
  w: Window = window;

  // ↓^9 Estructura definida por el usuario
  @Input() responsive: Responsive[];
  @Input() triggers: Triggers;
  @Input() headers: Headers;
  @Input() footers: Footers;
  @Input() options: Options;
  @Input() table: Tables;
  @Input() body: Body;

  // ↓ Pendiente al tamaño de la página para usar estructura responsiva
  @HostListener('window:resize', ['$event'])
  onResize(event: Event) { this.setResponsive((event.target as Window).innerWidth); }

  constructor(
    private sharedAsyncMethods: SharedAsyncMethodsService,
    private getHidingFields: GetDatatableHidingFieldsPipe,
    private getBooleanResult: GetBooleanResultPipe,
    private callApiMethods: CallApiMethodsService,
    private sharedMethods: SharedFunctionsService,
    public currentTheme: GetCurrentThemeService,
    public prettyTooltip: PrettyTooltipService,
    private normalizeText: NormalizeTextPipe,
    private uncommentHtml: UncommentHtmlPipe,
    private arraySlice: ArraySlicePipe,
    public setValue: SetValuePipe,
    private setFill: SetFillPipe,
    private builder: FormBuilder,
  ) {
    // ↓ Aisgna el id
    this.instanceId = this.instanceId ? this.instanceId : this.sharedMethods.generateHexString(8);
    // ↓ Asigna la configuración para todos los tamaños de ventana
    this.config.unshift({
      options: this.sharedMethods.getObjectNotReferenced(this.DatatableClass),
      breakpoint: Infinity,
      mousewheel: false,
    });
    // ↓ Referencia la configuración a usar
    this.datatable = { ...this.config[this.index].options };
  }

  ngOnInit() {
    // ↓ Comparte la suscripción al evento
    this.activate.emit({
      searchFormChanges: this.searchFormChanges,
      buttonClicked: this.buttonClicked,
      searchForm: this.searchForm,
      structure: this.structure,
      file: this.fileAction,
      row: this.rowAction
    });

    this.setSearchForm();
  }

  // setSearchForm(): Método que define el formulario de búsqueda
  async setSearchForm() {
    // ↓ Define la esrtuctura del formulario
    this.formOptions = this.builder.group({
      slice: [this.datatable.options.pager.limit, [Validators.pattern(REGEXP.onlyNumber), Validators.min(1)]],
      search: ["", [Validators.pattern(REGEXP.text)]]
    });

    await (() => new Promise<void>((resolve: () => void) => setTimeout(() => {
      // ↓ Pendiente a los cambios del buscador
      this.formOptions.controls.search.statusChanges.subscribe((status: string) => {
        // value: Valor ingresado en el buscador
        const value: string = this.formOptions.controls.search.value;
        // ↓↓ Elimina intervalo si existe
        this.datatable.options.searcher.interval ? clearTimeout(this.datatable.options.searcher.interval) : null;
        this.datatable.options.searcher.interval = null;
        // ↓ Verifica si es válido el valor
        if (status === "VALID") {
          this.datatable.options.searcher.interval = setTimeout(this.findInTable, this.datatable.options.searcher.timeInterval, value);
        }
      });
      resolve();
    }, 1)))();

    // ↓ Avisa que el formulario ha sido creado
    this.searchForm.next(true);
  }

  ngOnChanges(changes: SimpleChanges) {
    // options: Valores globales del datatable
    const options: Datatable = this.config.filter((elm: Responsive) => elm.breakpoint === Infinity)[0].options;
    // ↓ Asigna la configuración de la tabla
    if (changes.table) { options.table = this.setResponsiveChanges(options.table, changes.table.currentValue); }
    // ↓ Asigna la configuración de la cabecera
    if (changes.headers) { options.headers = this.setResponsiveChanges(options.headers, changes.headers.currentValue); }
    // ↓ Asigna la configuración del pié de página
    if (changes.footers) { options.footers = this.setResponsiveChanges(options.footers, changes.footers.currentValue); }
    // ↓ Asigna la configuración del cuerpo
    if (changes.body) {
      this.datatable.options.pager.page = 1;
      options.body = this.setResponsiveChanges(options.body, changes.body.currentValue);
    }
    // ↓ Asigna la configuración de las opciones
    if (changes.options) {
      // ↓ Asigna la configuración
      options.options = this.setResponsiveChanges(options.options, changes.options.currentValue);
      if (changes.options.currentValue) {
        // ↓ Verifica si se debe mostrar el formulario de descarga
        if (changes.options.currentValue.download && changes.options.currentValue.download.show) {
          // ↓ Si la solicitud no la realiza el usuario define las opciones
          this.filterOptions = this.builder.group({
            // ↓ Verifica que la entrada sea un booleano
            filters: [false, Validators.compose([this.sharedMethods.verifyBoolean])],
            // ↓ Verifica que la entrada no esté vacía y que cumpla con una expresión regular
            index: [0, Validators.compose([Validators.required, Validators.pattern(REGEXP.number)])]
          });
          // ↓ Espera a que las opciones estén definidas
          setTimeout(() => {
            // ↓* Configuraciones de materializecss
            const select: HTMLElement = document.querySelector(`#download-datatable${this.instanceId}`);
            const modal: HTMLElement = document.querySelector(`#modal-for-download-datatable${this.instanceId}`);
            M.Modal.init(modal, { inDuration: 400, outDuration: 500, onCloseEnd: () => { this.fileAction.next(false); } });
            M.FormSelect.init(select, {});
          }, 0);
        }
        // ↓ Verifica si está activa la opción de ver/ocultar columnas
        if (changes.options.currentValue.hiding && changes.options.currentValue.hiding.show) {
          // ↓ Espera a que las opciones estén definidas
          setTimeout(() => {
            // ↓* Configuraciones de materializecss
            // dropdown: Menú primer nvl
            const dropdown: NodeListOf<HTMLElement> = document.querySelectorAll(`#datatable-dropdown${this.instanceId} .datatable-dropdown`);
            M.Dropdown.init(dropdown, { coverTrigger: true, constrainWidth: false, closeOnClick: false });
            // dropdown2: Menú degundo nivel
            const dropdown2: NodeListOf<HTMLElement> = document.querySelectorAll(`#datatable-dropdown${this.instanceId} .datatable-dropdown2`);
            // ↓ Configuraciones materialize
            M.Dropdown.init(dropdown2, {
              constrainWidth: false,
              closeOnClick: false,
              coverTrigger: true,
              // onOpenStart(): Método que se ejecuta cuando se abre un menú
              onOpenStart: (el: Element) => {
                // ↓↓ Cierra los menús del mismo nivel que estén activos
                const elm: NodeListOf<HTMLElement> = document.querySelectorAll(`.${el.classList.value}[menuType=active]`);
                this.closeDropdowns(elm);
                // ↓ Marca el menú como activo
                el.setAttribute("menuType", "active");
              },
              // onCloseStart(): Método que se ejecuta cuando se cierra un menú
              onCloseStart: (el: Element) => {
                // ↓↓ Cierra los submenús asociados
                const elm: NodeListOf<HTMLElement> = document.querySelectorAll(`#${el.getAttribute("data-target")} a[menuType=active]`);
                this.closeDropdowns(elm);
              }
            });
          }, 0);
        }
      }
    }
    // ↓ Asigna la configuración del responsive
    if (changes.responsive) {
      // ↓ Asigna los cambios a los valores globales
      options.responsive = [...changes.responsive.currentValue];
      // ↓ Itera las opciones responsive
      changes.responsive.currentValue.forEach((elm: Responsive) => {
        // ↓ newOption: Copia de los valores globales
        const newOption: Responsive = { options: this.sharedMethods.getObjectNotReferenced(this.DatatableClass), breakpoint: elm.breakpoint || -1, mousewheel: elm.mousewheel || false };
        // ↓ Asigna la configuración responsive
        Object.keys(elm.options).forEach((el: string) => newOption.options[el] = { ...this.setResponsiveChanges(newOption.options[el], elm.options[el]) });
        // ↓ Verifica si existe una configuración para el punto dado
        const breakpoint: Responsive[] = this.config.filter((el: Responsive) => el.breakpoint === newOption.breakpoint);
        // ↓ Añade la configuración
        breakpoint.length > 0 ? (breakpoint[0] = { ...newOption }) : this.config.push({ ...newOption });
      });
    }
    // responsiveOptions: Valores responsivos del datatable
    const responsiveOptions: Responsive[] = this.config.filter((elm: Responsive) => elm.breakpoint !== Infinity);
    // ↓ Asigna los cambios a los valores responsivos
    responsiveOptions.forEach((elm: Responsive) => {
      // elmOptions: Configuración de la tabla para la opción responsive
      const elmOptions: Datatable = this.responsive.filter((el: Responsive) => +el.breakpoint === +elm.breakpoint)[0].options;
      // ↓↓ Asigna las modificaciones
      elm.options = { ...elm.options, ...this.sharedMethods.getObjectNotReferenced(options) };
      Object.keys(elmOptions).forEach((el: string) => this.setResponsiveChanges(elm.options[el], elmOptions[el]));
    });
    // ↓  Verifica si se debe realizar alguna acción
    if (changes.triggers) {
      // ↓ Verifica que no sea la primera vez
      if (changes.triggers.previousValue) {
        // ↓ Verifica si la acción a realizar es una descarga
        if (changes.triggers.currentValue.download !== changes.triggers.previousValue.download) {
          // ↓↓ Abre la ventana emergente
          const modal: HTMLElement = document.querySelector(`#modal-for-download-datatable${this.instanceId}`);
          M.Modal.getInstance(modal).open();
        }
        // ↓ Verifica si la acción a realizar es una obtención de los datos de descarga
        if (changes.triggers.currentValue.getStructure !== changes.triggers.previousValue.getStructure) {
          this.filterOptions.patchValue({ filters: false, index: 0 });
          this.downloadFile();
        }
        // ↓ Verifica si la acción a realizar es una actualización de la cabecera de la tabla
        if (this.datatable.headers.async.show) {
          if (changes.triggers.currentValue.getHeaders !== changes.triggers.previousValue.getHeaders) { this.updateAsyncData("headers"); }
        }
        // ↓ Verifica si la acción a realizar es una actualización del pié de página de la tabla
        if (this.datatable.footers.async.show) {
          if (changes.triggers.currentValue.getFooters !== changes.triggers.previousValue.getFooters) { this.updateAsyncData("footers"); }
        }
        // ↓ Verifica si la acción a realizar es una actualización del cuerpo de la tabla
        if (this.datatable.body.async.show) {
          if (changes.triggers.currentValue.getBody !== changes.triggers.previousValue.getBody) { this.updateAsyncData("body"); }
        }
      }
    }
    // ↓ Referencia la configuración a usar
    this.datatable = { ...this.config[this.index].options };
    // ↓ Verifica la configuración a usar
    this.setResponsive(self.innerWidth);
  }

  /* setResponsiveChanges(): Actualiza los atributos de la tabla para las opciones responsivas
    base: Valor por defecto
    changes: Valor a modificar
  */
  setResponsiveChanges = (base: any, changes: any) => (![undefined, null].includes(changes)) ? (typeof changes !== "object" ? changes : (Array.isArray(changes) ? [...changes] : (changes ? ((Object.keys(changes).forEach((el: string) => base[el] = this.setResponsiveChanges(base[el] || {}, changes[el])), { ...base })) : base))) : base;

  /* updateAsyncData(): Método que actualiza la consulta dinámica
    type: nombre de la propiedad a actualizar (headers, body, footers)
    filter: Filtrar coincidencia de valores
  */
  updateAsyncData(type: string) {
    // parms: Parámetros a gregar a la conulsta
    const parms: AlphanumericValues = {};
    if (type === "body") {
      if (this.formOptions.controls.search.status === "VALID" && this.formOptions.controls.search.value) {
        parms.buscador = JSON.stringify({ valor: this.normalizeText.transform(this.formOptions.controls.search.value.toLowerCase()), columnas: [] });
      }
      parms.limite = this.datatable.options.pager.limit;
      parms.pagina = this.datatable.options.pager.page;
    }
    this.callApiMethods.sendAsynchronousRequest("datatable", "getFields", { ...this.datatable[type].async.parameters, ...parms }, this.datatable[type].async.url)
      .then((data: AsyncData | ErrorStructure) => {
        if (!((data as ErrorStructure).requestStatus === false)) {
          if (type === "body") {
            // ↓ Asigna la cantidad de registros
            this.datatable.table.length = +(data as AsyncData).length;
          }
          // fields: Filtra los campos de acuerdo a las especificaciones
          const fields: (string | number | null)[][] = (data as AsyncData).fields.map((elm: ((string | number | null)[] | AlphanumericValues)) => {
            return this.datatable[type].async.fields.map((el: string): (string | number | null) => Object.keys(elm).includes(el) ? elm[el] : el);
          });
          // ↓↓ Actualiza los campos
          this.datatable[type].fields = [...fields];
          this[type].fields = [...fields];
        } else { M.toast({ html: "La consulta no es válida", classes: "background-danger white-text" }); }
      }).catch(this.callApiMethods.setError);
  }

  /* closeDropdowns(): Método que cierra los menús recibido
    elm: Menús
  */
  closeDropdowns(elm: NodeListOf<HTMLElement>) {
    // ↓ Verifica que exita 1 o más menús
    if (elm.length > 0) {
      // ↓* Itera los menús y por c/u elimina la marca de activo y lo cierra
      elm.forEach((element: HTMLElement) => {
        element.removeAttribute("menuType");
        M.Dropdown.getInstance(element).close();
      });
    }
  }

  /* setChanges(): Método que aplica la configuración del usuario a la estructura del datatable
    changes: Referencia de la configuración del usuario
    key: Referencia del atributo a modificar
  */
  setChanges(changes: any, key: any) {
    // ↓ Verifica que no sea un valor nulo
    if (changes) {
      // ↓ Itera los atributos a modificar
      Object.keys(changes).forEach((elm: string) => {
        // ↓ Aplica los cambios al atributo
        key[elm] = typeof key[elm] === "object" && !Array.isArray(key[elm]) ? (changes[elm] ? { ...key[elm], ...changes[elm] } : { ...{}, ...key[elm] }) : changes[elm];
      });
    }
  }

  /* setResponsive(): Método que selecciona la opción del datatable a usar de acuerdo al ancho de la página
    width: Ancho de la pagina
  */
  setResponsive(width: number) {
    // ↓ Verifica si hay opciones responsive definidas
    if (this.config.length > 1) {
      // ↓ Filtra las opciones con breakpoint mayor que el tamaño de la ventana
      const minors: number[] = this.config.map((elm: Responsive) => +elm.breakpoint)
        .filter((elm: number) => elm >= width)
        .sort((a: number, b: number) => +a - +b);
      // ↑ Ordena de menor a mayor los breakpoint filtrados
      // ↓ Asigna el índice de la opción con breakpoint menor
      this.index = this.config.findIndex((elm: Responsive) => +elm.breakpoint === +minors[0]);
      // elmOptions: Configuración de la tabla para la opción responsive
      const elmOptions: Datatable = this.config.filter((el: Responsive) => +el.breakpoint === +this.config[this.index].breakpoint)[0].options;
      // ↓^4 Actualiza los valores modificables por el usuario
      this.config[this.index].options.options.pager.limit = elmOptions.options && elmOptions.options.pager.limit ? elmOptions.options.pager.limit : this.datatable.options.pager.limit;
      this.config[this.index].options.options.searcher.interval = this.datatable.options.searcher.interval;
      this.config[this.index].options.body.fields = this.datatable.body.fields;
      // ↓ Referencia la configuración a usar
      this.datatable = { ...this.config[this.index].options };
      // ↓ Actualiza el formulario
      this.formOptions ? this.formOptions.controls.slice.setValue(this.datatable.options.pager.limit) : null;
      this.setCurrentPage(this.datatable.options.pager.page);
    }
  }

  /* setTableLength(): Método que modifica las opciones de la tabla para ajustarla al tamaño seleccionado por el usuario
    value: Aumenta/Disminuye el número de filas a mostrar por página
  */
  setTableLength(value: string) {
    // ↓ Combierte a número
    value = !value || isNaN(+value) ? "10" : value;
    // ↓ Verifica si el formulario es válido
    if (!this.formOptions.controls.slice.value) {
      this.formOptions.controls.slice.setValue(10);
    }
    if (this.formOptions.controls.slice.valid) {
      // ↓ Asigna el valor del formulario
      this.datatable.options.pager.limit = +value;
    } else {
      // ↓↓ Cambia y asinga el valor del formulario a 10
      this.formOptions.controls.slice.setValue(10);
      this.datatable.options.pager.limit = 10;
    }
    // ↓ Muestra la primera página
    this.setCurrentPage(1);
  }

  /* findInTable(): Método para buscar coincidencias en el listado
    value: Valor a buscar en la tabla
  */
  findInTable = (value: string) => {
    // ↓↓ Elimina el intervalo
    clearTimeout(this.datatable.options.searcher.interval);
    this.datatable.options.searcher.interval = null;
    if (this.datatable.body.async.show) {
      // ↓ Actualiza la información
      this.setCurrentPage(1);
    } else {
      // ↓ Muestra la tabla en la primera página
      this.datatable.options.pager.page = 1;
      // ↓ Ignora los caracteres especiales
      value = this.normalizeText.transform(value.toLowerCase());
      // filteredValues: Valores que cumplen con el valor especificado
      const filteredValues: Field[] = value.length > 0 ? (this.body.fields.filter((elm: Field, idx: number) => {
        // text: Une las columnas seleccionadas
        const text: string = this.normalizeText.transform(Object.keys(Object.values(elm).filter((el: string, ix: number) => (!this.options.searcher.column || this.options.searcher.column.length === 0 || this.options.searcher.column.includes('all')) ? true : (this.options.searcher.column.includes(`${ix}`)))).map((el: string, ix: number) => this.setFields(elm, this.datatable.body.rows[idx], idx, el, this.datatable.body.columns[ix], ix)).join("").toLowerCase());
        // ↓ Verifica si la union de las columnas contiene el valor del buscador
        return text.includes(value);
      })) : this.body.fields;
      // ↓ Asigna el resultado
      this.datatable.body.fields = [...filteredValues];
      this.searchFormChanges.next({ keywords: value, fields: this.datatable.body.fields, instance: this });
    }
  }

  /* simpleLoop(): Método para el loop de las iteraciones
    i: Índice de la fila
  */
  simpleLoop = (i: number) => i;

  // downloadFile(): Método que descarga la información de la tabla según las opciones seleccionadas
  downloadFile() {
    if (this.filterOptions.valid) {
      // selected: guarda la información del tipo de archivo seleccionado
      const selected: TableFile = this.datatable.options.download.files[this.filterOptions.value.index];
      // theme: Guarda los estilos actuales
      const theme: AnyValues = { ...this.currentTheme.themeValues, group: this.currentTheme.themeGroups };
      // datatableRules: Reglas definidas de la tabla
      const datatableRules: AnyValues = {
        hiding: this.getHidingFields.transform(this.datatable.options.hiding.groups),
        headers: { ...this.datatable.headers },
        footers: { ...this.datatable.footers },
        table: { ...this.datatable.table },
        body: { ...this.datatable.body },
        imgHeader: selected.imgHeader,
        preTable: selected.preTable,
      };
      delete datatableRules.headers.fields;
      delete datatableRules.footers.fields;
      delete datatableRules.body.fields;
      // styles: Convierte a texto para enviar como parámetro
      const rules: string = JSON.stringify(datatableRules);
      switch (selected.type) {
        // ↓ Verifica el archivo a descargar
        case "WORD":
          // ↓ Genera el archivo
          const bodyW: Field[] = this.filterOptions.value.filters ? ((this.datatable.options.pager.show && (!this.datatable.body.async.show)) ? (this.arraySlice.transform(this.datatable.body.fields, ((this.datatable.options.pager.page * +this.datatable.options.pager.limit) - +this.datatable.options.pager.limit), (this.datatable.options.pager.page * +this.datatable.options.pager.limit))) : this.datatable.body.fields) : this.body.fields;
          if (selected.extension === "get") {
            this.structure.next({
              table: { headers: [this.datatable.headers.fields], body: [bodyW], footers: [this.datatable.footers.fields] },
              headers: [this.setRows(this.datatable.headers.fields, "headers")],
              footers: [this.setRows(this.datatable.footers.fields, "footers")],
              body: [this.setRows(bodyW, "body")],
              params: { operation: "4", rules },
              download: `_${selected.name}`,
              sheets: selected.sheets || [],
              type: selected.extension,
              id: this.instanceId,
            });
          } else {
            this.sharedAsyncMethods.setDownloadParams(selected.extension, { operation: "4", rules }, [this.setRows(this.datatable.headers.fields, "headers")], [this.setRows(bodyW, "body")], selected.sheets || [], `_${selected.name}`, [this.setRows(this.datatable.footers.fields, "footers")], { headers: [this.datatable.headers.fields], body: [bodyW], footers: [this.datatable.footers.fields] });
          }
          break;
        case "Hoja de Cálculo":
          // ↓ Genera el archivo
          const body: Field[] = this.filterOptions.value.filters ? ((this.datatable.options.pager.show && (!this.datatable.body.async.show)) ? (this.arraySlice.transform(this.datatable.body.fields, ((this.datatable.options.pager.page * +this.datatable.options.pager.limit) - +this.datatable.options.pager.limit), (this.datatable.options.pager.page * +this.datatable.options.pager.limit))) : this.datatable.body.fields) : this.body.fields;
          if (selected.extension === "get") {
            this.structure.next({
              table: { headers: [this.datatable.headers.fields], body: [body], footers: [this.datatable.footers.fields] },
              headers: [this.setRows(this.datatable.headers.fields, "headers")],
              footers: [this.setRows(this.datatable.footers.fields, "footers")],
              body: [this.setRows(body, "body")],
              params: { operation: "3", rules },
              download: `_${selected.name}`,
              sheets: selected.sheets || [],
              type: selected.extension,
              id: this.instanceId,
            });
          } else {
            this.sharedAsyncMethods.setDownloadParams(selected.extension, { operation: "3", rules }, [this.setRows(this.datatable.headers.fields, "headers")], [this.setRows(body, "body")], selected.sheets || [], `_${selected.name}`, [this.setRows(this.datatable.footers.fields, "footers")], { headers: [this.datatable.headers.fields], body: [body], footers: [this.datatable.footers.fields] });
          }
          break;
        case "PDF":
          // ↓ Opciones de la tabla actuales
          const currentOptions: Options = { ...this.datatable.options };
          // ↓ Muestra todos los registros de la tabla
          if (!this.filterOptions.value.filters) {
            this.datatable.options = this.setResponsiveChanges(this.datatable.options, { pager: { show: false }, searcher: { show: false } });
          }
          setTimeout(() => {
            // text: Contenido HTML que se convertirá a pdf
            const text: string = document.querySelector(`#datatable-form${this.instanceId}`).innerHTML;
            // newContainer: Contenido HTML con los ajustes para generar pdf requerido
            const newContainer: HTMLElement = document.createElement("div");
            // ↓ Asigna el contenido HTML sin comentarios
            newContainer.innerHTML = `${selected.title || ''}<form>${this.uncommentHtml.transform(text)}<form>`;
            // ↓ Elimina algunos elementos que no se deben mostrar
            newContainer.querySelectorAll(".material-icons").forEach((elm: HTMLElement) => elm.remove());
            // ↓ pdfStyles: Estilos a aplicar
            const pdfStyles: string = JSON.stringify([`<style>
              form table tbody tr td.text-color-danger { color: red !important; text-align: center !important; }
              form table tbody tr td.text-color-success { color: green !important; text-align: center !important; }
              form table tbody tr td.text-color-warning { color: darkorange !important; text-align: center !important; }
              .bold { font-weight: 700; }
              form table thead tr td.background-theme-type-1 {
                background: ${this.currentTheme.themeGroups[0]} !important;
                color: ${this.currentTheme.themeValues.textColorOverBackground} !important;
              }
              form table thead tr td.background-theme-type-2 {
                background: ${this.currentTheme.themeGroups[1]} !important;
                color: ${this.currentTheme.themeValues.textColorOverBackground} !important;
              }
              form table thead tr td.background-theme-type-3 {
                background: ${this.currentTheme.themeGroups[2]} !important;
                color: ${this.currentTheme.themeValues.textColorOverBackground} !important;
              }
              form table thead tr td.background-theme-type-4 {
                background: ${this.currentTheme.themeGroups[3]} !important;
                color: ${this.currentTheme.themeValues.textColorOverBackground} !important;
              }
              form table thead tr td.using-theme {
                background: ${this.currentTheme.themeValues.background} !important;
                color: ${this.currentTheme.themeValues.textColorOverBackground} !important;
              }
              .row { width: 100%; }
              tbody tr:nth-child(odd) td:not(.background-color-danger):not(.background-color-warning):not(.background-color-success) {
                background-color: #eee;
              }
              h1, h2, h3, h4, h5, h6 { text-align: center; }
              .col { float: left; margin: 0 1.5%; }
              form table { width: 100%; border: 1px solid #ddd; font-size: 10px; margin-bottom: 5px; }
              form table thead tr td { border-bottom: 1px solid #ddd; text-align: center; }
              form table tbody tr td { border-right: 1px solid #ddd; text-align: center; }
              form table tfoot tr td { border-top: 1px solid #ddd; text-align: center; }
            </style>`]);
            // ↓ Genera el archivo
            if (selected.extension === "get") {
              this.structure.next({
                params: { operation: "2", pdfStyles },
                body: [newContainer.innerHTML],
                download: `_${selected.name}`,
                headers: ["gcgreportes.jpg"],
                type: selected.extension,
                id: this.instanceId,
              });
            } else {
              this.sharedAsyncMethods.setDownloadParams(selected.extension, { operation: "2", pdfStyles }, ["gcgreportes.jpg"], [newContainer.innerHTML], null, `_${selected.name}`);
            }
            // ↓ Devuelve la tabla a su estado natural
            this.datatable.options = currentOptions;
          }, 100);
          break;
      }
    }
  }

  /* setCurrentPage(): Método que actualiza la página
    page: Página a mostrar
    allowed: Permite realizar el cambio
  */
  setCurrentPage(page: number, allowed = true) {
    if (allowed) {
      // ↓ Actualiza la página
      this.datatable.options.pager.page = page;
      // ↓ Actualiza el contenido del datatable
      if (this.datatable.body.async.show && this.formOptions) { this.updateAsyncData("body"); }
    }
  }

  /* checkSelection(): Método que muestra/oculta columnas de la tabla
    value: Si está definido se mostrará la columna
    key: Elemento seleccionado
    type: Padre del elemento seleccionado
  */
  checkSelection(value: boolean, key: string, type: string | number) {
    // ↓ Verifica si se selcciona la opción de todos
    if (type === 0) {
      // ↓ Itera los índices del grupo
      Object.keys(this.datatable.options.hiding.groups[key]).forEach((elm: string) => {
        // ↓ Asigna el estado a cada índice
        this.datatable.options.hiding.groups[key][elm] = value;
      });
    } else { this.datatable.options.hiding.groups[type][key] = value; }
    // ↑ Asigna el estado al índice
    // ↓ Atualiza la referencia
    this.datatable.options.hiding.groups = JSON.parse(JSON.stringify(this.datatable.options.hiding.groups));
  }

  /* Método que formatea el valor de los campos de las filas del datatable
    rows: Filas
  */
  setRows = (rows: Field[], tag: string) => rows.map((row: Field, ir: number) => Object.keys(row).map((column: string, ic: number) => this.setFields(row, this.datatable[tag].rows[ir], ir, column, this.datatable[tag].columns[ic], ic)));

  /* setFields(): Método que formatea el valor de un campo de acuerdo a los parámetros del detatable
    rowValue: Columnas de la fila
    rowOpts: Opciones de las filas
    row: Índice de la fila
    colValue: Llave de la columna
    colOpts: Opciones de las columnas
    col: Índice de la columna
  */
  setFields(rowValue: Field, rowOpts: RowStructure, row: number, colValue: string | number, colOpts: ColumnStructure, col: number): string {
    return this.setValue.transform((rowOpts?.columns && rowOpts.columns[col]?.formatter && (rowOpts.columns[col].formatter.pipe || rowOpts.columns[col].formatter.text || rowOpts.columns[col].formatter.callback) ? (rowOpts.columns[col].formatter.pipe ? (rowOpts.columns[col].formatter.text ? (this.setValue.transform(this.setFill.transform(rowOpts.columns[col].formatter.text, rowValue, colValue), (rowOpts.columns[col].formatter.pipe.type), (rowOpts.columns[col].formatter.pipe.decimals || 2))) : (rowOpts.columns[col].formatter.callback ? (this.setValue.transform(this.setValue.transform([rowOpts.columns[col].formatter.callback, rowValue, rowValue[colValue], row, col], 'callback'), (rowOpts.columns[col].formatter.pipe.type), (rowOpts.columns[col].formatter.pipe.decimals || 2))) : (this.setValue.transform(rowValue[colValue], (rowOpts.columns[col].formatter.pipe.type), (rowOpts.columns[col].formatter.pipe.decimals || 2))))) : (rowOpts.columns[col].formatter.text ? (this.setFill.transform(rowOpts.columns[col].formatter.text, rowValue, colValue)) : (this.setValue.transform([rowOpts.columns[col].formatter.callback, rowValue, rowValue[colValue], row, col], 'callback')))) : (rowOpts?.formatter && (rowOpts.formatter.pipe || rowOpts.formatter.text || rowOpts.formatter.callback) ? (rowOpts.formatter.pipe ? (rowOpts.formatter.text ? (this.setValue.transform(this.setFill.transform(rowOpts.formatter.text, rowValue, colValue), (rowOpts.formatter.pipe.type), (rowOpts.formatter.pipe.decimals || 2))) : (rowOpts.formatter.callback ? (this.setValue.transform(this.setValue.transform([rowOpts.formatter.callback, rowValue, rowValue[colValue], row, col], 'callback'), (rowOpts.formatter.pipe.type), (rowOpts.formatter.pipe.decimals || 2))) : (this.setValue.transform(rowValue[colValue], (rowOpts.formatter.pipe.type), (rowOpts.formatter.pipe.decimals || 2))))) : (rowOpts.formatter.text ? (this.setFill.transform(rowOpts.formatter.text, rowValue, colValue)) : (this.setValue.transform([rowOpts.columns[col].formatter?.callback, rowValue, rowValue[colValue], row, col], 'callback')))) : (colOpts?.formatter && (colOpts.formatter.pipe || colOpts.formatter.text || colOpts.formatter.callback) ? (colOpts.formatter.pipe ? (colOpts.formatter.text ? (this.setValue.transform(this.setFill.transform(colOpts.formatter.text, rowValue, colValue), (colOpts.formatter.pipe.type), (colOpts.formatter.pipe.decimals || 2))) : (colOpts.formatter.callback ? (this.setValue.transform(this.setValue.transform([colOpts.formatter.callback, rowValue, rowValue[colValue], row, col], 'callback'), (colOpts.formatter.pipe.type), (colOpts.formatter.pipe.decimals || 2))) : (this.setValue.transform(rowValue[colValue], (colOpts.formatter.pipe.type), (colOpts.formatter.pipe.decimals || 2))))) : (colOpts.formatter.text ? (this.setFill.transform(colOpts.formatter.text, rowValue, colValue)) : (this.setValue.transform([colOpts.formatter?.callback, rowValue, rowValue[colValue], row, col], 'callback')))) : ((rowValue && (!(this.getBooleanResult.transform(colValue, '', '!!', 'str'))) && rowValue[colValue]) ? rowValue[colValue] : '')))), 'json');
  }

  // closeModal(): Método que cierra la ventana emergente con las opciones de descarga
  closeModal() {
    // ↓↓ Cierra el modal
    const modal: HTMLElement = document.querySelector('#modal-for-download-datatable' + this.instanceId);
    M.Modal.getInstance(modal).close();
  }

  ngOnDestroy() {
    // ↓↓ Asegurarse de que los intervalos se eliminan
    this.datatable.options.searcher.interval ? clearTimeout(this.datatable.options.searcher.interval) : null;
    this.datatable.options.searcher.interval = null;
  }

}
